import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  structuredData?: object;
  canonical?: string;
  noindex?: boolean;
  alternateLanguages?: Array<{ lang: string; url: string }>;
}

const SEO: React.FC<SEOProps> = ({
  title = 'GoTruTax - AI-Powered Tax Compliance & Management Platform',
  description = 'GoTruTax is the most advanced AI-powered tax compliance and management platform. Automate tax calculations, ensure compliance across all US states, and manage nexus with intelligent automation.',
  keywords = [
    'tax compliance',
    'tax management',
    'sales tax',
    'nexus determination',
    'tax automation',
    'business tax software',
    'multi-state tax',
    'tax calculation',
    'compliance software',
    'sales tax reporting',
    'sales tax nexus',
    'economic nexus',
    'marketplace facilitator',
    'remote seller',
    'tax software',
    'accounting software',
    'small business tax',
    'ecommerce tax',
    'platform integration',
    'Shopify tax',
    'QuickBooks tax'
  ],
  image = 'https://gotrutax.com/hero-image.png',
  url = 'https://gotrutax.com',
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  section,
  tags,
  structuredData,
  canonical,
  noindex = false,
  alternateLanguages
}) => {
  const siteName = 'GoTruTax';
  const twitterHandle = '@gotrutax';
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const fullUrl = url.startsWith('http') ? url : `https://gotrutax.com${url}`;
  const fullImage = image.startsWith('http') ? image : `https://gotrutax.com${image}`;

  // Generate structured data for the website
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    'name': 'GoTruTax',
    'description': description,
    'url': 'https://gotrutax.com',
    'applicationCategory': 'BusinessApplication',
    'operatingSystem': 'Web Browser',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
      'description': 'Free Monitor tier available, Standard plan $49/month, Pro plan $149/month'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'GoTruTax',
      'url': 'https://gotrutax.com'
    },
    'featureList': [
      'AI-Powered Tax Calculations',
      'Multi-State Tax Compliance',
      'Nexus Determination',
      'Sales Tax Reporting',
      'Platform Integrations',
      'Real-time Compliance Monitoring',
      'Tax Audit Protection',
      'Multi-company Management'
    ],
    'screenshot': 'https://gotrutax.com/hero-image.png'
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={author || 'GoTruTax Team'} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1'} />
      <meta name="googlebot" content={noindex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1'} />
      <meta name="bingbot" content={noindex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1'} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonical || fullUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Article specific Open Graph tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && section && (
        <meta property="article:section" content={section} />
      )}
      {type === 'article' && tags && tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="application-name" content={siteName} />

      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Alternate Language Links */}
      {alternateLanguages && alternateLanguages.map(({ lang, url: langUrl }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={langUrl} />
      ))}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>

      {/* Additional structured data for organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          'name': 'GoTruTax',
          'url': 'https://gotrutax.com',
          'logo': 'https://gotrutax.com/logo.svg',
          'description': 'AI-powered tax compliance and management platform for businesses',
          'foundingDate': '2024',
          'contactPoint': {
            '@type': 'ContactPoint',
            'telephone': '+1-555-123-4567',
            'contactType': 'customer service',
            'availableLanguage': 'English'
          },
          'sameAs': [
            'https://twitter.com/gotrutax',
            'https://linkedin.com/company/gotrutax',
            'https://github.com/gotrutax'
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
